import {
    AssessmentSchedule as APIAssessmentSchedule,
    Assessment,
    Location,
    ResourceAssessmentScheduleAssociation
} from "../../../API";
import React, {
    ReactNode,
    createContext,
    useContext,
    useMemo,
} from 'react';

import { AssessmentSchedule } from "../../../models";
import { ResourceNotificationSettingDraft } from "../../../lib/dashboard/types/ResourceNotificationSettingDraft";

interface AssessmentsContext {
    assessments: Array<Assessment>;
    assessmentSchedules: Array<APIAssessmentSchedule>;
    resourceAssessmentScheduleAssociations: Array<ResourceAssessmentScheduleAssociation>;
    fetchAssessmentScheduleStatus?: "idle" | "error" | "loading" | "success";
    assessmentScheduleIdToResourceIdsMap?: Map<string, string[]> | undefined;
    createAssessmentSchedule?: (schedule: AssessmentSchedule, selectedLocationIds: Array<string>, missedAssessmentNotificationSetting: ResourceNotificationSettingDraft) => Promise<void>;
    onEditScheduleSubmit?: (schedule: AssessmentSchedule, selectedLocationIds: Array<string>, missedAssessmentNotificationSetting: ResourceNotificationSettingDraft) => Promise<void>;
    onDeleteAssessmentSchedule?: (assessmentSchedule: AssessmentSchedule, associatedLocations: Location[]) => Promise<void>;
    onEditAssessmentSubmit?: (assessmentId: string, assessment: Assessment) => Promise<void>;
}

const AssessmentsContext = createContext<AssessmentsContext | undefined>(undefined);

export const AssessmentsContextProvider: React.FC<{ children: ReactNode; value: Omit<AssessmentsContext, "assessmentScheduleIdToResourceIdsMap">; }> = ({ children, value }) => {
    const assessmentScheduleIdToResourceIdsMap = useMemo(() => {
        return value.resourceAssessmentScheduleAssociations?.reduce((map, association) => {
            if (!association.assessmentScheduleId || !association.resourceId) {
                return map;
            }
            const resourceIds = map.get(association.assessmentScheduleId) || [];
            resourceIds.push(association.resourceId);
            map.set(association.assessmentScheduleId, resourceIds);
            return map;
        }, new Map<string, string[]>());
    }, [value.resourceAssessmentScheduleAssociations]);

    return (
        <AssessmentsContext.Provider value={{ ...value, assessmentScheduleIdToResourceIdsMap }}>
            {children}
        </AssessmentsContext.Provider>
    );
};

export const useAssessments = (): AssessmentsContext => {
    const context = useContext(AssessmentsContext);
    if (!context) {
        throw new Error('useAssessments must be used within a assessmentsContextProvider');
    }
    return context;
};